/** UAE ISD digits (no +) — unified OTP + delivery rules */
export const UAE_ISD_DIGITS = "971";

/** India ISD digits (no +) */
export const INDIA_ISD_DIGITS = "91";

const DEFAULT_ISD = "+91";

/**
 * Normalize stored ISD to `+{digits}` for unified OTP APIs.
 */
export function normalizeIsdCode(isd: string | null | undefined): string {
  const raw = (isd ?? "").trim();
  if (!raw) return DEFAULT_ISD;
  if (raw.startsWith("+")) return raw;
  return `+${raw.replace(/\D/g, "")}`;
}

export function isUaeCountryCode(countryCode: string): boolean {
  return countryCode.replace(/\D/g, "") === UAE_ISD_DIGITS;
}

export function isIndiaCountryCode(countryCode: string): boolean {
  return countryCode.replace(/\D/g, "") === INDIA_ISD_DIGITS;
}

/** `mm:ss` countdown for resend UI */
export function formatOtpCountdownTimer(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function getInitialsFromName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function isRemoteProfilePhotoUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

export function buildDisplayName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallback: string,
): string {
  const combined = [firstName?.trim(), lastName?.trim()]
    .filter(Boolean)
    .join(" ")
    .trim();
  return combined || fallback;
}

const DEFAULT_INITIAL_OTP_DEDUP_MS = 3500;
let lastInitialOtpKey: string | null = null;
let lastInitialOtpAt = 0;

/**
 * Dedupes the *automatic* first generate-OTP call (React Strict Mode remount, effect replay).
 * Do not use for user-initiated resend.
 */
export function shouldSkipDuplicateInitialOtpSend(
  key: string,
  windowMs: number = DEFAULT_INITIAL_OTP_DEDUP_MS,
): boolean {
  const now = Date.now();
  if (lastInitialOtpKey === key && now - lastInitialOtpAt < windowMs) {
    return true;
  }
  lastInitialOtpKey = key;
  lastInitialOtpAt = now;
  return false;
}

export type OtpMode = "SMS" | "WA";

/** Default channel for first send from country / ISD */
export function defaultOtpModeForCountry(countryCode: string): OtpMode {
  return isUaeCountryCode(countryCode) ? "WA" : "SMS";
}

/** Best-effort message extraction from unified OTP / axios-style errors */
export function parseUnifiedOtpFlowError(err: unknown): string | null {
  const e = err as {
    response?: { data?: { detail?: string; message?: string } };
    message?: string;
  };
  const detail = e?.response?.data?.detail;
  const msg = e?.response?.data?.message;
  if (typeof detail === "string" && detail) return detail;
  if (typeof msg === "string" && msg) return msg;
  if (typeof e?.message === "string" && e.message) return e.message;
  return null;
}
